import { useState, useEffect, useCallback, DependencyList, useRef } from 'react';

export function useAsyncData<TData>(
  fetchFn: (signal: AbortSignal) => Promise<TData>,
  initialData: TData | null = null,
  deps: DependencyList = [],
) {
  const [data, setData] = useState(initialData);
  const [isInProgress, setIsInProgress] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isShown = useRef<boolean>(false);

  const fetchData = useCallback(async () => {
    const showTimer = setTimeout(() => {
      isShown.current = true;
      setIsLoading(true);
    }, 500);

    // Cancel previous ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    const timeoutTimer = setTimeout(() => abortControllerRef.current.abort(), 10000);

    try {
      setIsInProgress(true);

      const result = await fetchFn(signal);

      if (!signal.aborted) {
        setData(result);
        setError(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      clearTimeout(timeoutTimer);

      if (!signal.aborted) {
        setIsInProgress(false);
      }

      if (isShown.current) {
        setTimeout(() => {
          if (!signal.aborted) {
            setIsLoading(false);
          }
        }, 1000);
      } else {
        clearTimeout(showTimer);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData, isInProgress };
}
